<!--
  PacksPage · 课程包列表页（路由 /packs）
  - 展示所有课程包（含 default 默认包）
  - 新建课程包（名称 + 描述）
  - 点击进入编辑页 /packs/:id/edit
-->
<template>
  <div class="packs-page">
    <div class="page-head">
      <h2>📚 课程包</h2>
      <button class="primary" @click="showCreate = !showCreate">+ 新建课程包</button>
    </div>

    <!-- 新建表单 -->
    <div v-if="showCreate" class="create-box">
      <input v-model.trim="newName" placeholder="课程包名称（如：走遍美国 第一季）" maxlength="60" />
      <input v-model.trim="newDesc" placeholder="描述（可选）" maxlength="120" />
      <button class="primary" @click="onCreate" :disabled="!newName">创建</button>
      <button @click="showCreate = false; newName = ''; newDesc = ''">取消</button>
    </div>

    <!-- 课程包卡片 -->
    <div v-if="loading" class="empty">加载中…</div>
    <div v-else-if="!packs.length" class="empty">
      还没有任何课程包，点右上角「+ 新建课程包」开始吧 🎬
    </div>
    <div v-else class="pack-grid">
      <div v-for="p in packs" :key="p.id" class="pack-card" @click="onOpen(p)">
        <div class="pack-card-head">
          <span class="pack-name">{{ p.name }}</span>
          <span v-if="p.id === 'default'" class="tag builtin">默认</span>
        </div>
        <div class="pack-desc">{{ p.desc || '（无描述）' }}</div>
        <div class="pack-meta">
          <span class="chip">{{ p.lesson_count || 0 }} 节课</span>
          <span class="chip">{{ fmtDate(p.created_at) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { api, toast } from '@/composables/usePlayerState'

const router = useRouter()
const packs = ref([])
const loading = ref(true)
const showCreate = ref(false)
const newName = ref('')
const newDesc = ref('')

async function loadPacks() {
  loading.value = true
  try {
    const data = await api('/api/course-packs')
    packs.value = data.items || []
  } catch (e) {
    toast('加载失败：' + e.message)
  } finally {
    loading.value = false
  }
}

async function onCreate() {
  if (!newName.value) return
  try {
    const fd = new FormData()
    fd.append('name', newName.value)
    fd.append('desc', newDesc.value)
    const r = await api('/api/course-packs', { method: 'POST', body: fd })
    toast('已创建：' + r.name)
    showCreate.value = false; newName.value = ''; newDesc.value = ''
    await loadPacks()
    router.push('/packs/' + r.id + '/edit')
  } catch (e) {
    toast('创建失败：' + e.message)
  }
}

function onOpen(p) {
  router.push('/packs/' + p.id + '/edit')
}

function fmtDate(ts) {
  if (!ts) return ''
  const d = new Date(ts * 1000)
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

onMounted(loadPacks)
</script>

<style scoped>
.packs-page { display: flex; flex-direction: column; gap: 16px; }
.page-head {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
}
.page-head h2 { margin: 0; font-size: 22px; }
.create-box {
  display: flex; gap: 8px; flex-wrap: wrap;
  padding: 14px; border: 1px solid var(--line); border-radius: 12px;
  background: var(--panel);
}
.create-box input { flex: 1; min-width: 200px; }
.empty {
  padding: 40px; text-align: center; color: var(--mute);
  border: 1px dashed var(--line); border-radius: 12px;
}
.pack-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
}
.pack-card {
  padding: 16px; border: 1px solid var(--line); border-radius: 14px;
  background: var(--panel); backdrop-filter: blur(10px);
  cursor: pointer; transition: all .15s ease;
}
.pack-card:hover {
  border-color: var(--accent);
  transform: translateY(-2px);
  box-shadow: 0 8px 28px #6ea8ff22;
}
.pack-card-head {
  display: flex; align-items: center; gap: 8px; margin-bottom: 8px;
}
.pack-name { font-size: 16px; font-weight: 600; }
.tag.builtin {
  font-size: 11px; padding: 2px 8px; border-radius: 999px;
  background: #2a499e; color: #fff;
}
.pack-desc {
  color: var(--mute); font-size: 13px; min-height: 38px;
  margin-bottom: 10px; line-height: 1.5;
}
.pack-meta { display: flex; gap: 6px; flex-wrap: wrap; }
</style>
